import React, { useState, useEffect } from 'react';
import { Wrench, Calendar, Clock, Car, CheckCircle2, ChevronLeft, Search, MapPin, Phone, Loader2, Globe, X, User } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { OpeningDayPicker } from '../components/OpeningDayPicker';
import './BookingPage.css';

const DAG_ORDER = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

const STR = {
  nl: {
    notAvailable: 'Online afspraken zijn niet (meer) beschikbaar voor deze garage.',
    loadError: 'Kon de boekingspagina niet laden.', loading: 'Laden…',
    step1: 'Dienst', step2: 'Datum & tijd', step3: 'Jouw gegevens',
    q1: 'Wat voor werk wil je inplannen?', min: 'min', back: 'Terug',
    q2: 'Kies datum & tijd', datum: 'Datum', avail: 'Beschikbare tijden',
    availLoading: 'Beschikbaarheid laden…', noSlots: 'Geen beschikbare tijden op deze dag. Kies een andere datum.',
    closedHint: 'Gesloten dagen zijn doorgestreept en kun je niet kiezen.',
    verder: 'Verder', q3: 'Jouw gegevens',
    naam: 'Naam', naamPh: 'Voor- en achternaam', tel: 'Telefoon', email: 'E-mail', emailPh: 'naam@email.nl',
    kenteken: 'Kenteken', rdw: 'RDW-check', rdwNotFound: 'Kenteken niet gevonden bij RDW', apkTot: 'APK tot',
    opmerking: 'Opmerking / klacht', opmerkingPh: 'Beschrijf kort wat er moet gebeuren…',
    submit: 'Afspraak aanvragen', submitting: 'Versturen…',
    fineprint: 'Dit is een aanvraag. {garage} bevestigt je afspraak per e-mail.',
    successTitle: 'Aanvraag verzonden!',
    successBody: 'Bedankt {voornaam}. Je aanvraag voor {dienst} op {datum} om {tijd} is ontvangen.',
    successEmail: 'Je ontvangt een e-mail op {email}. Zodra {garage} je afspraak bevestigt, krijg je een definitieve bevestiging.',
    at: 'om', hours: 'Openingstijden', gesloten: 'Gesloten', poweredBy: 'Mogelijk gemaakt door',
    portaalTitle: 'Al klant bij ons?', portaalLink: 'Ga naar het klantportaal',
    portaalSub: 'Bekijk je afspraken, facturen en APK-status',
    genericError: 'Er ging iets mis. Probeer het opnieuw.',
    days: { ma: 'Maandag', di: 'Dinsdag', wo: 'Woensdag', do: 'Donderdag', vr: 'Vrijdag', za: 'Zaterdag', zo: 'Zondag' },
  },
  en: {
    notAvailable: 'Online booking is not (or no longer) available for this garage.',
    loadError: 'Could not load the booking page.', loading: 'Loading…',
    step1: 'Service', step2: 'Date & time', step3: 'Your details',
    q1: 'What would you like to book?', min: 'min', back: 'Back',
    q2: 'Choose date & time', datum: 'Date', avail: 'Available times',
    availLoading: 'Loading availability…', noSlots: 'No available times on this day. Please choose another date.',
    closedHint: 'Closed days are struck through and cannot be selected.',
    verder: 'Continue', q3: 'Your details',
    naam: 'Name', naamPh: 'First and last name', tel: 'Phone', email: 'Email', emailPh: 'name@email.com',
    kenteken: 'License plate', rdw: 'RDW check', rdwNotFound: 'Plate not found at RDW', apkTot: 'MOT until',
    opmerking: 'Note / complaint', opmerkingPh: 'Briefly describe what needs to be done…',
    submit: 'Request appointment', submitting: 'Sending…',
    fineprint: 'This is a request. {garage} will confirm your appointment by email.',
    successTitle: 'Request sent!',
    successBody: 'Thanks {voornaam}. Your request for {dienst} on {datum} at {tijd} has been received.',
    successEmail: 'You will receive an email at {email}. Once {garage} confirms your appointment, you will get a final confirmation.',
    at: 'at', hours: 'Opening hours', gesloten: 'Closed', poweredBy: 'Powered by',
    portaalTitle: 'Already a customer?', portaalLink: 'Go to the customer portal',
    portaalSub: 'View your appointments, invoices and MOT status',
    genericError: 'Something went wrong. Please try again.',
    days: { ma: 'Monday', di: 'Tuesday', wo: 'Wednesday', do: 'Thursday', vr: 'Friday', za: 'Saturday', zo: 'Sunday' },
  },
};

export default function BookingPage({ garageId }) {
  const { t, lang, setLang, languages, autoDetected, dismissAutoDetected } = useT();
  const L = React.useMemo(() => {
    if (STR[lang]) return STR[lang];
    const out = {};
    for (const [k, v] of Object.entries(STR.nl)) {
      if (k === 'days') continue;
      out[k] = typeof v === 'string' ? t(v) : v;
    }
    out.days = Object.fromEntries(Object.entries(STR.nl.days).map(([dk, dv]) => [dk, t(dv)]));
    return out;
  }, [lang, t]);
  const fmt = (s, vars) => Object.entries(vars || {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), s);

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [step, setStep] = useState(1);
  const [dienst, setDienst] = useState(null);
  const [datum, setDatum] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [tijd, setTijd] = useState('');
  const [form, setForm] = useState({ naam: '', telefoon: '', email: '', kenteken: '', opmerking: '' });
  const [rdw, setRdw] = useState(null);
  const [rdwBusy, setRdwBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    carsysApi.getPublicBooking(garageId)
      .then(setInfo)
      .catch(e => setErr(e.response?.status === 404 ? L.notAvailable : L.loadError));
  }, [garageId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dienst || !datum) { setSlots([]); return; }
    setSlotsLoading(true); setTijd('');
    carsysApi.getPublicSlots(garageId, datum, dienst.id)
      .then(r => setSlots(r.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [dienst, datum, garageId]);

  const checkRdw = async () => {
    const kt = (form.kenteken || '').trim();
    if (!kt) return;
    setRdwBusy(true); setRdw(null);
    try {
      const v = await carsysApi.publicRdwLookup(kt);
      setRdw({ ok: true, label: `${v.merk} ${v.handelsbenaming}`.trim(), apk: v.vervaldatumApk });
    } catch {
      setRdw({ ok: false, label: L.rdwNotFound });
    } finally { setRdwBusy(false); }
  };

  const submit = async () => {
    if (!form.naam.trim() || !form.email.includes('@')) return;
    setSubmitting(true);
    try {
      const res = await carsysApi.createPublicBooking(garageId, {
        dienstId: dienst.id, datum, tijd, lang,
        naam: form.naam, telefoon: form.telefoon, email: form.email,
        kenteken: form.kenteken, opmerking: form.opmerking,
      });
      setDone(res);
    } catch (e) {
      if (e.response?.status === 409) {
        setStep(2); setTijd('');
        carsysApi.getPublicSlots(garageId, datum, dienst.id).then(r => setSlots(r.slots || []));
      }
      alert(e.response?.data?.detail || L.genericError);
    } finally { setSubmitting(false); }
  };

  const LangSwitch = () => (
    <div style={{ position: 'relative' }}>
      <div className="bk-lang" data-testid="booking-lang-switch">
        {languages.map(l => (
          <button key={l.code} data-testid={`booking-lang-${l.code}`} className={lang === l.code ? 'active' : ''} onClick={() => setLang(l.code)}>{(l.code || '').toUpperCase()}</button>
        ))}
      </div>
      {autoDetected && (
        <div data-testid="booking-lang-autodetect-hint"
          style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 30, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', background: '#0ea5e9', color: '#fff', padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, boxShadow: '0 6px 20px rgba(0,0,0,.25)' }}>
          <Globe size={14} />
          <span>{t('We tonen deze site in jouw taal — wijzig hier')}</span>
          <button onClick={dismissAutoDetected} data-testid="booking-lang-autodetect-dismiss" aria-label={t('Sluiten')}
            style={{ background: 'rgba(255,255,255,.25)', border: 'none', borderRadius: 5, color: '#fff', cursor: 'pointer', display: 'flex', padding: 2 }}>
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );

  if (err && !info) return (
    <div className="bk-wrap"><div className="bk-shell">
      <div className="bk-card bk-msg" data-testid="booking-error">{err}</div>
      <a href={`/portaal/${encodeURIComponent(garageId)}`} data-testid="booking-portaal-link"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, padding: '14px 16px', background: '#2563eb', color: '#fff', borderRadius: 12, textDecoration: 'none' }}>
        <span>
          <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{L.portaalTitle}</span>
          <span style={{ display: 'block', fontSize: 12.5, opacity: 0.9 }}>{L.portaalSub}</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.18)', padding: '8px 12px', borderRadius: 999, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
          <User size={14} /> {L.portaalLink}
        </span>
      </a>
    </div></div>
  );
  if (!info) return (
    <div className="bk-wrap"><div className="bk-shell"><div className="bk-card bk-msg"><Loader2 className="bk-spin" size={20} /> {L.loading}</div></div></div>
  );

  return (
    <div className="bk-wrap" data-testid="booking-page" style={{ '--bk-accent': info.accent || '#7c3aed', '--bk-accent2': info.accent || '#7c3aed' }}>
      <div className="bk-shell">
        <header className="bk-head">
          <div className="bk-logo">{info.heeftLogo && info.slug ? <img src={carsysApi.showroomLogoUrl(info.slug)} alt="logo" onError={e => { e.target.style.display = 'none'; }} /> : <Wrench size={20} />}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 data-testid="booking-garage-naam">{info.garageNaam}</h1>
            <div className="bk-sub">
              {info.adres && <span><MapPin size={12} /> {info.adres}{info.plaats ? `, ${info.plaats}` : ''}</span>}
              {info.telefoon && <span><Phone size={12} /> {info.telefoon}</span>}
            </div>
          </div>
          <LangSwitch />
        </header>

        {done ? (
          <div className="bk-card bk-success" data-testid="booking-success">
            <CheckCircle2 size={48} className="bk-check" />
            <h2>{L.successTitle}</h2>
            <p>{fmt(L.successBody, { voornaam: form.naam.split(' ')[0], dienst: done.dienst, datum: done.datum, tijd: done.tijd })}</p>
            <p className="bk-muted">{fmt(L.successEmail, { email: form.email, garage: info.garageNaam })}</p>
          </div>
        ) : (
          <>
            <div className="bk-steps">
              {[L.step1, L.step2, L.step3].map((s, i) => (
                <div key={i} className={`bk-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                  <span className="bk-step-n">{step > i + 1 ? '✓' : i + 1}</span> {s}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div className="bk-card" data-testid="booking-step-dienst">
                <h2><Wrench size={17} /> {L.q1}</h2>
                <div className="bk-diensten">
                  {info.diensten.map(d => (
                    <button key={d.id} data-testid={`booking-dienst-${d.id}`} className={`bk-dienst ${dienst?.id === d.id ? 'sel' : ''}`}
                      onClick={() => { setDienst(d); setStep(2); }}>
                      <span className="bk-dienst-naam">{d.naam}</span>
                      <span className="bk-dienst-duur"><Clock size={12} /> ± {d.duurMin} {L.min}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="bk-card" data-testid="booking-step-datum">
                <button className="bk-back" onClick={() => setStep(1)}><ChevronLeft size={15} /> {L.back}</button>
                <h2><Calendar size={17} /> {L.q2}</h2>
                <div className="bk-chosen">{dienst?.naam} · ± {dienst?.duurMin} {L.min}</div>
                <label className="bk-label">{L.datum}</label>
                <OpeningDayPicker
                  openingstijden={info.openingstijden}
                  value={datum}
                  onChange={setDatum}
                  maxDays={90}
                  lang={lang}
                  testid="booking-datum"
                  placeholder={L.datum}
                  triggerStyle={bkDateTrigger}
                />
                <div className="bk-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{L.closedHint}</div>
                {datum && (
                  <>
                    <label className="bk-label">{L.avail}</label>
                    {slotsLoading ? (
                      <div className="bk-muted"><Loader2 className="bk-spin" size={15} /> {L.availLoading}</div>
                    ) : slots.length === 0 ? (
                      <div className="bk-empty" data-testid="booking-no-slots">{L.noSlots}</div>
                    ) : (
                      <div className="bk-slots">
                        {slots.map(s => (
                          <button key={s} data-testid={`booking-slot-${s}`} className={`bk-slot ${tijd === s ? 'sel' : ''}`} onClick={() => setTijd(s)}>{s}</button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <button className="bk-next" data-testid="booking-next-details" disabled={!datum || !tijd} onClick={() => setStep(3)}>{L.verder}</button>
              </div>
            )}

            {step === 3 && (
              <div className="bk-card" data-testid="booking-step-gegevens">
                <button className="bk-back" onClick={() => setStep(2)}><ChevronLeft size={15} /> {L.back}</button>
                <h2><Car size={17} /> {L.q3}</h2>
                <div className="bk-summary">{dienst?.naam} · {datum} {L.at} {tijd}</div>
                <div className="bk-grid">
                  <div className="bk-col2">
                    <label className="bk-label">{L.naam} *</label>
                    <input className="bk-input" data-testid="booking-naam" value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })} placeholder={L.naamPh} />
                  </div>
                  <div>
                    <label className="bk-label">{L.tel}</label>
                    <input className="bk-input" data-testid="booking-telefoon" value={form.telefoon} onChange={e => setForm({ ...form, telefoon: e.target.value })} placeholder={lang === 'en' ? '+31 6...' : '06...'} />
                  </div>
                  <div>
                    <label className="bk-label">{L.email} *</label>
                    <input className="bk-input" type="email" data-testid="booking-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={L.emailPh} />
                  </div>
                  <div className="bk-col2">
                    <label className="bk-label">{L.kenteken}</label>
                    <div className="bk-kenteken-row">
                      <input className="bk-input bk-plate" data-testid="booking-kenteken" value={form.kenteken} onChange={e => { setForm({ ...form, kenteken: e.target.value.toUpperCase() }); setRdw(null); }} placeholder="12-AB-34" />
                      <button className="bk-rdw" data-testid="booking-rdw-btn" onClick={checkRdw} disabled={rdwBusy || !form.kenteken.trim()}>
                        {rdwBusy ? <Loader2 className="bk-spin" size={14} /> : <Search size={14} />} {L.rdw}
                      </button>
                    </div>
                    {rdw && <div className={`bk-rdw-result ${rdw.ok ? 'ok' : 'bad'}`} data-testid="booking-rdw-result">{rdw.ok ? `✓ ${rdw.label}${rdw.apk ? ` · ${L.apkTot} ${rdw.apk}` : ''}` : rdw.label}</div>}
                  </div>
                  <div className="bk-col2">
                    <label className="bk-label">{L.opmerking}</label>
                    <textarea className="bk-input" rows={3} data-testid="booking-opmerking" value={form.opmerking} onChange={e => setForm({ ...form, opmerking: e.target.value })} placeholder={L.opmerkingPh} />
                  </div>
                </div>
                <button className="bk-next" data-testid="booking-submit" disabled={submitting || !form.naam.trim() || !form.email.includes('@')} onClick={submit}>
                  {submitting ? <><Loader2 className="bk-spin" size={15} /> {L.submitting}</> : L.submit}
                </button>
                <p className="bk-fineprint">{fmt(L.fineprint, { garage: info.garageNaam })}</p>
              </div>
            )}
          </>
        )}

        <div className="bk-hours" data-testid="booking-hours">
          <h3>{L.hours}</h3>
          {DAG_ORDER.map(d => {
            const ot = info.openingstijden[d] || {};
            return (
              <div key={d} className="bk-hour-row">
                <span>{L.days[d]}</span>
                <span>{ot.actief ? `${ot.open} – ${ot.dicht}` : L.gesloten}</span>
              </div>
            );
          })}
        </div>
        <a className="bk-portaal" href={`/portaal/${encodeURIComponent(garageId)}`} data-testid="booking-portaal-link"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, padding: '14px 16px', background: 'var(--bk-accent)', color: '#fff', borderRadius: 12, textDecoration: 'none' }}>
          <span>
            <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{L.portaalTitle}</span>
            <span style={{ display: 'block', fontSize: 12.5, opacity: 0.9 }}>{L.portaalSub}</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.18)', padding: '8px 12px', borderRadius: 999, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            <User size={14} /> {L.portaalLink}
          </span>
        </a>
        <div className="bk-foot">
          {L.poweredBy} <b>MonteurCentraal</b>
          <div style={{ marginTop: 6 }}>
            <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>{t('Privacyverklaring')}</a>
            {' · '}
            <a href="/voorwaarden" target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>{t('Voorwaarden')}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const bkDateTrigger = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  width: '100%', padding: '12px 14px', borderRadius: 12,
  border: '1px solid rgba(255,255,255,.16)', background: 'rgba(255,255,255,.04)',
  color: '#e2e8f0', fontSize: 15, cursor: 'pointer', textAlign: 'left',
};
